import React, { useState, useEffect } from 'react';
import axiosInstance from '../../api/Axios';
import {
  Container,
  Row,
  Col,
  Card,
  CardTitle,
  CardBody,
  Table,
  Button,
  Input,
  Spinner,
  Alert,
  Badge,
  Collapse
} from 'reactstrap';

const InstitutionRow = ({ institution, campuses, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    const confirmMessage = `Are you sure you want to delete ${institution.name}?\n\nThis will also delete ${campuses.length} campus(es) associated with this institution.\n\nThis action cannot be undone.`;
    
    if (window.confirm(confirmMessage)) {
      setDeleting(true);
      try {
        await axiosInstance.delete(`api/institution_delete/${institution.id}/`);
        onDelete(institution.id);
      } catch (error) {
        console.error('Error deleting institution:', error);
        alert('Failed to delete institution. Please try again.');
        setDeleting(false);
      }
    }
  };

  return (
    <>
      <tr>
        <td style={{ width: '50px' }}>
          <Button
            color="link"
            size="sm"
            onClick={() => setOpen(!open)}
          >
            <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
          </Button>
        </td>
        <td>
          <i className="bi bi-building me-2"></i>
          <strong>{institution.name}</strong>
        </td>
        <td>
          <Badge color="primary">{institution.abbrev}</Badge>
        </td>
        <td>{institution.address || 'N/A'}</td>
        <td>{institution.country}</td>
        <td><small>{institution.partner_code}</small></td>
        <td className="text-center">
          <Badge color="secondary">{campuses.length}</Badge>
        </td>
        <td className="text-center">
          <Button
            color="danger"
            size="sm"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Spinner size="sm" className="me-1" />
                Deleting...
              </>
            ) : (
              <>
                <i className="bi bi-trash me-1"></i>
                Delete
              </>
            )}
          </Button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan="7" style={{ padding: '0' }}>
            <div style={{ backgroundColor: '#f8f9fa', padding: '15px' }}>
              <h6 className="text-primary mb-3">
                <i className="bi bi-geo-alt me-2"></i>
                Campuses ({campuses.length})
              </h6>
              <Table size="sm" bordered hover>
                <thead>
                  <tr>
                    <th>Campus Name</th>
                    <th>Address</th>
                    <th>SEQ</th>
                    <th>Partner Code</th>
                  </tr>
                </thead>
                <tbody>
                  {campuses.map((campus) => (
                    <tr key={campus.id}>
                      <td>{campus.name}</td>
                      <td>{campus.address || 'N/A'}</td>
                      <td><Badge color="info" pill>{campus.seq}</Badge></td>
                      <td><small>{campus.partner_code}</small></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const Institutions = () => {
  const [institutions, setInstitutions] = useState([]);
  const [campusesByInstitution, setCampusesByInstitution] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all institutions
      const institutionsResponse = await axiosInstance.get('api/institution_view/');
      const institutionsData = institutionsResponse.data;
      setInstitutions(institutionsData);

      // Fetch campuses for each institution
      const campusesData = {};
      await Promise.all(
        institutionsData.map(async (institution) => {
          try {
            const campusResponse = await axiosInstance.get(
              `api/institutions/${institution.id}/campuses/`
            );
            campusesData[institution.id] = campusResponse.data;
          } catch (err) {
            console.error(`Error fetching campuses for institution ${institution.id}:`, err);
            campusesData[institution.id] = [];
          }
        })
      );

      setCampusesByInstitution(campusesData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load institutions and campuses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInstitution = (institutionId) => {
    // Remove institution from state
    setInstitutions(institutions.filter(inst => inst.id !== institutionId));
    // Remove its campuses from state
    const newCampuses = { ...campusesByInstitution };
    delete newCampuses[institutionId];
    setCampusesByInstitution(newCampuses);
  };

  const filteredInstitutions = institutions.filter((institution) =>
    (institution.name && institution.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (institution.abbrev && institution.abbrev.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (institution.partner_code && institution.partner_code.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalCampuses = Object.values(campusesByInstitution).reduce(
    (sum, campuses) => sum + campuses.length,
    0
  );

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
        <h6 className="mt-3">Loading institutions and campuses...</h6>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert color="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4 mb-4">
      <Row className="mb-4">
        <Col>
          <h4>
            <i className="bi bi-building me-2"></i>
            Institutions & Campuses
          </h4>
          <p className="text-muted">
            Manage all educational institutions and their respective campuses
          </p>
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card>
            <CardBody className="d-flex align-items-center">
              <i className="bi bi-building" style={{ fontSize: '2.5rem', color: '#0d6efd' }}></i>
              <div className="ms-3">
                <h3 className="text-primary mb-0">{institutions.length}</h3>
                <small className="text-muted">Total Institutions</small>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <CardBody className="d-flex align-items-center">
              <i className="bi bi-geo-alt" style={{ fontSize: '2.5rem', color: '#6c757d' }}></i>
              <div className="ms-3">
                <h3 className="text-secondary mb-0">{totalCampuses}</h3>
                <small className="text-muted">Total Campuses</small>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <CardBody className="d-flex align-items-center">
              <i className="bi bi-mortarboard" style={{ fontSize: '2.5rem', color: '#198754' }}></i>
              <div className="ms-3">
                <h3 className="text-success mb-0">{filteredInstitutions.length}</h3>
                <small className="text-muted">Universities</small>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Search Bar */}
      <Row className="mb-3">
        <Col>
          <Input
            type="text"
            placeholder="🔍 Search by institution name, abbreviation, or partner code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
      </Row>

      {/* Institutions Table */}
      <Row>
        <Col>
          <Card>
            <CardBody>
              <div style={{ maxHeight: '700px', overflowY: 'auto' }}>
                <Table hover responsive>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                    <tr>
                      <th style={{ width: '50px' }}></th>
                      <th>Institution Name</th>
                      <th>Abbreviation</th>
                      <th>Province/Address</th>
                      <th>Country</th>
                      <th>Partner Code</th>
                      <th className="text-center">Campuses</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInstitutions.map((institution) => (
                      <InstitutionRow
                        key={institution.id}
                        institution={institution}
                        campuses={campusesByInstitution[institution.id] || []}
                        onDelete={handleDeleteInstitution}
                      />
                    ))}
                  </tbody>
                </Table>
              </div>
              {filteredInstitutions.length === 0 && (
                <div className="text-center mt-4">
                  <h6 className="text-muted">
                    No institutions found matching your search.
                  </h6>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Institutions;
